import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  HelpAnchor,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { RingStat } from '@/versions/v1/shared/viz/RingStat';
import { unpackTable } from '@/versions/v1/shared/columnar';
import { useOemData, type WorkOrder } from '@/versions/v1/shared/useOemData';
import { jobs as buildJobs, type OemJob } from '@/versions/v1/shared/oem-engine';
import { MODULE_BY_ID } from '@/versions/v1/shared/seed/modules';
import { WORKFLOW_STATUS_LABEL, type WorkflowStatus } from '@/versions/v1/shared/seed/workflows';
import { MeasureTile, SceneSection, SourceLink, TonePill } from '@/versions/v1/shared/oem-tiles';
import { useToast } from '@/versions/v1/shared/toast/ToastContext';
import {
  TRACE_MODULE,
  defaultPhaseIndex,
  resolveTrace,
  traceHeader,
  traceTask,
  tracePicker,
} from './CaseTraceScene.transform';

// =========================================================================
// Case-to-closure trace. Route `/workflows/case-trace`. Fork-owned (zone C).
//
// Port of the demo's `renderWorkflow` (source line 2366).
//
// This is the page that answers "can you show me?". One case, followed
// through every phase, with what happened at each step and who did it — the
// audit trail a customer can be walked through line by line. Three cases are
// written out by hand; every other job on the board generates its own trace
// from the module it has reached, so the page is never a dead end.
//
// Selection lives in the URL (`?case=`, `?phase=`) so a phase of a trace is a
// link, which is what makes this usable in a demo script.
//
// Two small demo behaviours are kept. Clicking a work-stream ring highlights
// it and its task-board column; the first ring is highlighted when a phase
// opens, as in the demo. "+ Add task" under each column shows a toast and
// writes nothing — nothing on this proof of concept writes back.
//
// UI layer.
// =========================================================================

/** The board, so the picker can offer a generated run for any open job. */
function useBoardJobs(): OemJob[] | null {
  const workOrders = useOemData('work-orders');
  const service = useOemData('service');
  return useMemo(() => {
    if (!workOrders.data || !service.data) return null;
    return buildJobs({
      workOrders: unpackTable<WorkOrder>(workOrders.data.wos),
      techJobs: service.data.tapp_jobs,
      cases: service.data.cases,
    });
  }, [workOrders.data, service.data]);
}

/** Phase / step status → the ring tone and the pill tone. */
const RING_TONE: Readonly<Record<WorkflowStatus, string>> = {
  done: 'good',
  now: 'warn',
  todo: 'flat',
};
const PILL_TONE: Readonly<Record<WorkflowStatus, 'good' | 'warn' | 'flat'>> = {
  done: 'good',
  now: 'warn',
  todo: 'flat',
};

export function CaseTraceScene() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const jobs = useBoardJobs();

  const trace = useMemo(() => resolveTrace(params.get('case'), jobs), [params, jobs]);
  const picker = useMemo(() => tracePicker(jobs), [jobs]);
  const header = useMemo(() => traceHeader(trace.workflow), [trace]);

  const phases = trace.workflow.phases;
  // `Number(null)` is 0, so an absent `?phase=` must be tested before it is
  // parsed — otherwise every trace opens on phase 1 instead of on the phase
  // that is actually in progress.
  const phaseParam = params.get('phase');
  const requested = phaseParam === null ? Number.NaN : Number(phaseParam);
  const selected =
    Number.isInteger(requested) && requested >= 0 && requested < phases.length
      ? requested
      : defaultPhaseIndex(trace.workflow);
  const phase = phases[selected];

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value === null) next.delete(key);
      else next.set(key, value);
      setParams(next);
    },
    [params, setParams],
  );

  /** Changing the case resets the phase — phase 6 of one case is not phase 6 of another. */
  const pickCase = useCallback(
    (value: string) => {
      const next = new URLSearchParams(params);
      next.set('case', value);
      next.delete('phase');
      setParams(next);
    },
    [params, setParams],
  );

  // The highlighted ring is local to one phase of one case: a new phase
  // starts on its first ring again, as the demo re-rendered with `j===0`.
  const { show: toast } = useToast();
  const ringScope = `${trace.key}|${selected}`;
  const [ringPick, setRingPick] = useState<{ scope: string; index: number } | null>(null);
  const activeRing = ringPick?.scope === ringScope ? ringPick.index : 0;

  const module = MODULE_BY_ID[TRACE_MODULE];

  return (
    <SurfacePageLayout width="full">
      <Commentable name="CaseTraceScene">
        <SurfacePageBody className="@container max-w-[1400px] space-y-8 pt-6">
          <SurfacePageHeader
            kicker={`${String(module.n).padStart(2, '0')} ${module.name}`}
            title="Case-to-closure trace"
            description="One case followed end to end through all ten modules, with what happened at each step, which agent or technician did it, and what is still queued. Phases across the top, the selected phase's work streams in the middle, its tasks underneath."
            actions={<HelpAnchor doc="workflows/case-trace" />}
            meta={
              <span className="flex flex-wrap items-center gap-2 text-[11.5px]">
                <Link
                  to="/workflows/steps/intake"
                  className="no-underline"
                  style={{ color: 'var(--accent)' }}
                >
                  Case intake &amp; triage ›
                </Link>
                <span style={{ color: 'var(--ink-tertiary)' }}>·</span>
                <Link to="/job-board" className="no-underline" style={{ color: 'var(--accent)' }}>
                  Job board ›
                </Link>
              </span>
            }
          />

          <AskStrip />

          {/* ------------------------------------------------------- */}
          {/* Picker                                                  */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Picker">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex min-w-0 flex-1 flex-col gap-1">
                <span
                  className="text-[10px] font-semibold uppercase tracking-[0.08em]"
                  style={{ color: 'var(--ink-tertiary)' }}
                >
                  Case
                </span>
                <select
                  value={trace.key}
                  onChange={(e) => pickCase(e.target.value)}
                  className="h-9 max-w-full rounded-lg border px-2 text-[12.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                  style={{
                    borderColor: 'var(--hairline)',
                    background: 'var(--surface-2)',
                    color: 'var(--ink-primary)',
                  }}
                >
                  <optgroup label="Worked traces">
                    {picker
                      .filter((p) => p.worked)
                      .map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                  </optgroup>
                  <optgroup label="Generated from the job board">
                    {picker
                      .filter((p) => !p.worked)
                      .map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </label>
              <TonePill tone={trace.worked ? 'good' : 'flat'}>
                {trace.worked
                  ? 'Worked trace · written step by step'
                  : 'Generated from the job’s current module'}
              </TonePill>
            </div>
          </Commentable>

          {/* ------------------------------------------------------- */}
          {/* Hero figures                                            */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Header">
            <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2 @5xl:grid-cols-4">
              <MeasureTile eyebrow="Case" value={header.caseNo} detail={header.caseName} />
              <MeasureTile
                eyebrow="Machine"
                value={header.machine}
                detail={header.machineDetail}
                onSelect={
                  header.machine === '—'
                    ? undefined
                    : () => navigate(`/machines/${encodeURIComponent(header.machine)}`)
                }
                selectHint="Open the machine page"
              />
              <MeasureTile
                eyebrow="Progress"
                value={`${header.overall}%`}
                detail={`${header.phasesDone} of ${header.phaseCount} phases complete`}
                tone={header.overall === 100 ? 'good' : 'warn'}
              />
              <MeasureTile
                eyebrow="Phases traced"
                value={header.phaseCount}
                detail="detect through to close-out and cost"
              />
            </div>
          </Commentable>

          {/* ------------------------------------------------------- */}
          {/* Phase strip                                             */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Phases">
            <SceneSection
              title="Case trace"
              subtitle={`${phases.length} phases · ${trace.workflow.ref}`}
              actions={
                <span className="mono text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                  {`${header.overall}% overall`}
                </span>
              }
            >
              <div
                aria-hidden="true"
                className="mb-3 h-1.5 overflow-hidden rounded-full"
                style={{ background: 'var(--hairline)' }}
              >
                <span
                  className="block h-full rounded-full"
                  style={{ width: `${header.overall}%`, background: 'var(--accent)' }}
                />
              </div>
              <ul className="grid list-none grid-cols-2 gap-2 p-0 @2xl:grid-cols-4 @5xl:grid-cols-5">
                {phases.map((p, i) => (
                  <li key={`${p.name}-${i}`} className="min-w-0">
                    <button
                      type="button"
                      aria-current={i === selected ? 'step' : undefined}
                      onClick={() => setParam('phase', String(i))}
                      className="flex h-full w-full flex-col items-start gap-1 rounded-xl border px-3 py-2.5 text-left transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                      style={{
                        borderColor:
                          i === selected ? 'var(--accent)' : 'var(--hairline)',
                        background:
                          i === selected ? 'var(--accent-soft)' : 'var(--surface-2)',
                      }}
                    >
                      <span className="flex w-full items-baseline justify-between gap-2">
                        <span
                          className="mono text-[10.5px]"
                          style={{ color: 'var(--ink-tertiary)' }}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span
                          className="mono text-[11px] font-semibold"
                          style={{ color: 'var(--ink-secondary)' }}
                        >
                          {`${p.pct}%`}
                        </span>
                      </span>
                      <span
                        className="block text-[12px] font-medium leading-snug"
                        style={{ color: 'var(--ink-primary)' }}
                      >
                        {p.name}
                      </span>
                      <TonePill tone={PILL_TONE[p.status]}>
                        {WORKFLOW_STATUS_LABEL[p.status]}
                      </TonePill>
                    </button>
                  </li>
                ))}
              </ul>
            </SceneSection>
          </Commentable>

          {/* ------------------------------------------------------- */}
          {/* Selected phase — work streams                           */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Phase">
            <SceneSection
              title={`${String(selected + 1).padStart(2, '0')} · ${phase.name}`}
              subtitle={`${WORKFLOW_STATUS_LABEL[phase.status].toLowerCase()} · ${phase.pct}%`}
            >
              <div className="flex flex-wrap gap-4">
                {phase.steps.map((step, j) => (
                  <button
                    key={`${step.name}-${j}`}
                    type="button"
                    aria-pressed={j === activeRing}
                    onClick={() => setRingPick({ scope: ringScope, index: j })}
                    className="flex w-[13rem] flex-none flex-col items-center gap-2 rounded-xl border px-3 py-4 text-center transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                    style={{
                      borderColor: j === activeRing ? 'var(--accent)' : 'var(--hairline)',
                      background: j === activeRing ? 'var(--accent-soft)' : 'var(--surface-2)',
                    }}
                  >
                    <RingStat
                      pct={step.pct}
                      tone={RING_TONE[step.status]}
                      size={72}
                      label={String(step.pct)}
                      sub="%"
                      title={`${step.name} progress`}
                    />
                    <span
                      className="text-[12px] font-medium leading-snug"
                      style={{ color: 'var(--ink-primary)' }}
                    >
                      {step.name}
                    </span>
                    <TonePill tone={PILL_TONE[step.status]}>
                      {`${WORKFLOW_STATUS_LABEL[step.status]} · ${step.tasks.length}`}
                    </TonePill>
                  </button>
                ))}
              </div>
            </SceneSection>
          </Commentable>

          {/* ------------------------------------------------------- */}
          {/* Task board                                              */}
          {/* ------------------------------------------------------- */}
          <Commentable name="Tasks">
            <SceneSection
              title="Task board"
              subtitle={`phase ${String(selected + 1).padStart(2, '0')} · ${phase.name}`}
            >
              <div className="overflow-x-auto pb-2">
                <div className="flex min-w-max gap-3">
                  {phase.steps.map((step, j) => (
                    <div key={`${step.name}-col-${j}`} className="w-[19rem] flex-none">
                      <h3
                        className="mb-2 border-b-2 pb-1 text-[12px] font-semibold"
                        style={{
                          color: 'var(--ink-primary)',
                          borderColor: j === activeRing ? 'var(--accent)' : 'transparent',
                        }}
                      >
                        {step.name}
                      </h3>
                      <div className="flex flex-col gap-2">
                        {step.tasks.map((raw, k) => {
                          const task = traceTask(raw, step.status);
                          return (
                            <article
                              key={`${step.name}-${k}`}
                              className="rounded-xl border px-3 py-2.5"
                              style={{
                                borderColor: 'var(--hairline)',
                                background: task.agent
                                  ? 'var(--accent-soft)'
                                  : 'var(--surface-2)',
                              }}
                            >
                              <p
                                className="m-0 text-[12px] font-semibold leading-snug"
                                style={{ color: 'var(--ink-primary)' }}
                              >
                                {task.lead}
                              </p>
                              {task.detail ? (
                                <p
                                  className="m-0 mt-1 text-[11.5px] leading-[1.5]"
                                  style={{ color: 'var(--ink-secondary)' }}
                                >
                                  {task.detail}
                                </p>
                              ) : null}
                              <p
                                className="m-0 mt-2 flex items-center justify-between gap-2 text-[10.5px]"
                                style={{ color: 'var(--ink-tertiary)' }}
                              >
                                <span>{task.who}</span>
                                <span>{task.state}</span>
                              </p>
                            </article>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => toast(`Task added to ${step.name}`)}
                          className="w-full rounded-lg border border-dashed px-3 py-1.5 text-center text-[11.5px] font-medium transition-colors hover:bg-[var(--surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
                          style={{ borderColor: 'var(--hairline-strong)', color: 'var(--ink-secondary)' }}
                        >
                          + Add task
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <p
                className="mt-3 text-[11.5px] leading-[1.6]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                {trace.worked
                  ? 'This case is written out step by step from the records behind it.'
                  : 'This trace is generated from the module the job has reached: everything before it is complete, its own module carries the job’s progress, and the job’s next action is the first task of that module.'}
              </p>
            </SceneSection>
          </Commentable>

          {trace.workflow.machine ? (
            <Commentable name="Footer">
              <p className="text-[12px]" style={{ color: 'var(--ink-secondary)' }}>
                <SourceLink
                  onClick={() =>
                    navigate(`/machines/${encodeURIComponent(trace.workflow.machine)}`)
                  }
                  className="mono"
                >
                  {trace.workflow.machine}
                </SourceLink>
                {` · ${trace.workflow.ref}`}
              </p>
            </Commentable>
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

export default CaseTraceScene;
